import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import * as mouseflow from "@utils/mouseflow";
import {
  Button,
  Heading,
  Pill,
  SkeletonBase,
  Table,
  Pager,
  CardNotification,
  TableColumn,
  Text,
} from "@foris/avocado-suite";
import {
  GroupEnrollment,
  GroupsManagerEnrollmentsOrderByFieldsEnum,
  GroupsManagerEnrollmentsSearchableFieldsEnum,
  PageInfo,
} from "@models/ISchema";
import { ContextEDH } from "@context/ContextEDH";
import { getColumnCell } from "@common/utils/table.util";
import DimensionsSearchSkeleton from "@components/dimensions-search-skeleton";
import { useTranslation } from "react-i18next";
import AutocompleteSelector from "@common/components/autocomplete-selector";
import i18n from "@config/i18n";
import css from "./enrollmentsTable.module.scss";
import {
  EnrollmentsTableFiltersState,
  setSearchBy,
  useEnrollmentsTableFilters,
} from "../../context/useEnrollmentsTableFilters";
import GroupTableCourseColumn from "@modules/vacancies/Groups/group-table-course-column";

const searchByOptions = () => [
  { label: i18n.t("groups-manager.enrollments-table.search-by.all"), value: "all" },
  {
    label: i18n.t("groups-manager.enrollments-table.search-by.campus"),
    value: GroupsManagerEnrollmentsSearchableFieldsEnum.Campus,
  },
  {
    label: i18n.t("groups-manager.enrollments-table.search-by.program"),
    value: GroupsManagerEnrollmentsSearchableFieldsEnum.Program,
  },
  {
    label: i18n.t("groups-manager.enrollments-table.search-by.student"),
    value: GroupsManagerEnrollmentsSearchableFieldsEnum.Student,
  },
  {
    label: i18n.t("groups-manager.enrollments-table.search-by.group"),
    value: GroupsManagerEnrollmentsSearchableFieldsEnum.Group,
  },
  {
    label: i18n.t("groups-manager.enrollments-table.search-by.state"),
    value: GroupsManagerEnrollmentsSearchableFieldsEnum.State,
  },
];

const getSearchByPayload = (
  query: string,
  field: GroupsManagerEnrollmentsSearchableFieldsEnum | "all",
): EnrollmentsTableFiltersState["searchBy"] => {
  if (!query || !field) {
    return {
      fields: [],
      text: "",
    };
  }

  if (field === "all") {
    const fields =
      Object.values(searchByOptions())
        ?.map(option => option?.value)
        ?.filter(option => option !== "all") ?? [];

    return {
      fields: fields as GroupsManagerEnrollmentsSearchableFieldsEnum[],
      text: query,
    };
  }

  return {
    fields: [field],
    text: query,
  };
};

interface IProps {
  reportUrl: string;
  enrollments: GroupEnrollment[];
  enrollmentsPageInfo: PageInfo;
  showStudentsGroup?: boolean;
  pageSize?: number;
  isLoading?: boolean;
  lastUpdateDateTime?: string;
  requestEnrollments: (page?: number, pageSize?: number) => void;
}

const EnrollmentsTable = (props: IProps) => {
  const { t } = useTranslation();
  const { state: outerState } = useContext(ContextEDH);
  const {
    reportUrl,
    enrollments,
    enrollmentsPageInfo,
    isLoading,
    pageSize,
    showStudentsGroup,
    lastUpdateDateTime,
    requestEnrollments,
  } = props;
  const { state } = useContext(ContextEDH);
  const searchBy = useEnrollmentsTableFilters(state => state.searchBy);
  const isLoadingRequest = isLoading || !enrollmentsPageInfo;
  const [isMounted, setIsMounted] = useState(false);
  const [currentSearchBy, setCurrentSearchBy] = useState<{ query: string; option: any } | null>(
    null,
  );

  const isDefaultBehavior = state?.base?.groupsManagerType === "SHARED";

  useEffect(() => {
    if (isMounted) {
      requestEnrollments(1);
    } else {
      setIsMounted(true);
    }
  }, [searchBy]);

  const columns = useMemo(() => {
    const items: TableColumn[] = [
      {
        key: GroupsManagerEnrollmentsOrderByFieldsEnum.Student,
        header: t("groups-manager.enrollments-table.table.headers.student"),
        renderCell: (enrollment: GroupEnrollment) => {
          const code = enrollment?.student?.code;
          const name = enrollment?.student?.name;

          return getColumnCell(`${code} - ${name}`);
        },
      },
      {
        key: GroupsManagerEnrollmentsOrderByFieldsEnum.Program,
        header: t("groups-manager.enrollments-table.table.headers.program"),
        renderCell: ({ program }: GroupEnrollment) => {
          const { code, name } = program;
          return getColumnCell(`${code}${code && name ? " | " : ""}${name}`);
        },
      },
      !isDefaultBehavior
        ? {
            key: GroupsManagerEnrollmentsOrderByFieldsEnum.Campus,
            header: t("groups-manager.enrollments-table.table.headers.campus"),
            renderCell: ({ campus }: GroupEnrollment) => {
              const { code, name } = campus;
              return getColumnCell(`${code}${code && name ? " | " : ""}${name}`);
            },
          }
        : {
            key: "course",
            isSortable: false,
            header: t("groups-manager.enrollments-table.table.headers.course"),
            renderCell: (row: unknown) => {
              const enrollment = row as GroupEnrollment;

              if ("isResume" in enrollment) {
                return "";
              }

              const props = {
                groupLabel: `${enrollment?.group?.course?.code} - ${enrollment?.group?.course?.name}`,
                termCode: enrollment?.term?.code,
                campusCode: enrollment?.campus?.code,
                departmentCode: enrollment?.program?.department?.code,
                modalityCode: enrollment?.program?.modality?.code,
                shiftCode: enrollment?.program?.shift?.code,
                programCode: enrollment?.program?.code,
                curriculumCode: enrollment?.group?.course?.curriculum?.code,
              };

              return <GroupTableCourseColumn {...props} hasOnboarding={false} />;
            },
            desktopOptions: {
              headCellStyles: {
                width: "380px",
                minWidth: "100px",
              },
            },
          },
      {
        key: "waitingList",
        header: t("groups-manager.enrollments-table.table.headers.status"),
        renderCell: (enrollment: GroupEnrollment) => {
          return (
            <Pill className={css.tablePill}>
              {enrollment?.isWaitingList
                ? t("groups-manager.enrollments-table.table.rows.status.waiting")
                : t("groups-manager.enrollments-table.table.rows.status.enrolled")}
            </Pill>
          );
        },
      },
      {
        key: "enrollmentDate",
        header: t("groups-manager.enrollments-table.table.headers.date"),
        renderCell: (enrollment: GroupEnrollment) => {
          const showDate = !enrollment?.isWaitingList;
          return getColumnCell(showDate ? enrollment?.enrollmentDate : "-");
        },
        desktopOptions: { headCellStyles: { minWidth: "180px" } },
      },
    ];

    if (showStudentsGroup) {
      items.splice(1, 0, {
        key: GroupsManagerEnrollmentsOrderByFieldsEnum.Group,
        header: t("groups-manager.enrollments-table.table.headers.group"),
        renderCell: row => {
          const enrollment = row as GroupEnrollment;
          return getColumnCell(enrollment?.group?.code ?? "-");
        },
        desktopOptions: { headCellStyles: { minWidth: "80px" } },
      });
    }

    return items;
  }, [showStudentsGroup]);

  const handleDownloadReport = useCallback(() => {
    window.open(reportUrl, "_blank");
    mouseflow.actionTag(
      "action_groups_manager_enrollments_report",
      outerState?.base?.isMouseflowEnabled,
    );
  }, [reportUrl, outerState?.base?.isMouseflowEnabled]);

  if (!enrollments?.length && !!enrollmentsPageInfo && !currentSearchBy && !isLoadingRequest) {
    return (
      <CardNotification
        className={css.noResults}
        state="info"
        title={t("groups-manager.enrollments-table.empty-results.title")}
        outlined
      >
        {t("groups-manager.enrollments-table.empty-results.description")}
      </CardNotification>
    );
  }

  return (
    <section className={css.enrollmentsTable}>
      <header className={css.enrollmentsTable_header}>
        <Heading className={css.headerTitle} type="h2">
          {!enrollmentsPageInfo || isLoadingRequest ? (
            <SkeletonBase width={225} height={24} radius={4} />
          ) : (
            t("groups-manager.enrollments-table.count-title", {
              count: enrollmentsPageInfo?.total ?? 0,
            })
          )}
        </Heading>

        <div className={css.headerActions}>
          {!!lastUpdateDateTime && (
            <Text className={css.headerActions_lastUpdate}>
              <b>{t("groups-manager.groups-table.last-update")}:</b> {lastUpdateDateTime}
            </Text>
          )}

          <Button
            size="sm"
            variant="ghost"
            leftIcon="refresh"
            disabled={isLoadingRequest}
            onClick={() => requestEnrollments(enrollmentsPageInfo?.page ?? 1)}
          >
            {t("groups-manager.enrollments-table.update-button")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            leftIcon="download"
            onClick={handleDownloadReport}
            disabled={isLoadingRequest}
          >
            {t("groups-manager.enrollments-table.download-report")}
          </Button>
        </div>
      </header>

      <div className={css.enrollmentsTable_table}>
        {isLoadingRequest && (
          <DimensionsSearchSkeleton
            rows={pageSize ?? enrollmentsPageInfo?.size ?? 5}
            columns={columns?.length}
            showHeader={false}
            showPagination={
              enrollmentsPageInfo?.hasNextPage || enrollmentsPageInfo?.hasPreviousPage
            }
          />
        )}

        {!isLoadingRequest && !!enrollmentsPageInfo && (
          <AutocompleteSelector
            className={css.tableSearch}
            initialOption={currentSearchBy?.option ?? undefined}
            initialInputValue={currentSearchBy?.query ?? undefined}
            isClearable={!!currentSearchBy?.query}
            options={searchByOptions()}
            onSearch={payload => {
              const newSearchBy = getSearchByPayload(
                payload?.query,
                payload?.option?.value as GroupsManagerEnrollmentsSearchableFieldsEnum | "all",
              );
              setSearchBy(newSearchBy);
              setCurrentSearchBy(payload);
            }}
            onClearSearch={() => {
              setSearchBy({ fields: [], text: "" });
              setCurrentSearchBy(null);
            }}
          />
        )}

        {!isLoadingRequest && !enrollments?.length && !!currentSearchBy && (
          <CardNotification
            className={css.noResults}
            state="info"
            title={t("groups-manager.enrollments-table.empty-results.title")}
            outlined
          >
            {t("groups-manager.enrollments-table.empty-results.description-search")}
          </CardNotification>
        )}

        {!isLoadingRequest && !!enrollments?.length && (
          <>
            <Table columns={columns} data={enrollments ?? []} />
            <Pager
              classname={{
                global: css.tablePager,
              }}
              size={pageSize}
              page={enrollmentsPageInfo?.page}
              total={enrollmentsPageInfo?.total ?? 0}
              showRowsPerPage={pageSize !== undefined}
              pageSizeOptions={[5, 10, 20]}
              onChange={requestEnrollments}
              onRowsPerPageChange={option => requestEnrollments(1, option.value)}
            />
          </>
        )}
      </div>
    </section>
  );
};

export default EnrollmentsTable;
